import { DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  InjectionToken,
  input,
  Signal,
} from '@angular/core';

export interface EvaluationScoreResult {
  score: number;
  maxScore: number;
  coverageAnalysis?: string;
  coherenceJustification?: string;
  coherenceWarning?: string;
}

export interface CompiledElementEvaluationProvider {
  evaluate(widgetId: string): Signal<{
    score: number;
    maxScore: number;
    coverageAnalysis?: string;
    coherenceJustification?: string;
    coherenceWarning?: string;
  } | null>;
}

export const COMPILED_ELEMENT_EVALUATION_PROVIDER =
  new InjectionToken<CompiledElementEvaluationProvider>(
    'COMPILED_ELEMENT_EVALUATION_PROVIDER',
  );

/** An evaluation result question, keyed by the widget id it scores. */
type ScoredQuestion = EvaluationScoreResult & { question: string };

/**
 * The slice of an evaluation result this lookup needs. Typed structurally on
 * purpose: `TenderFormEvaluation` exists in two divergent copies (the app's
 * `core/api` DTO and the library's `api/interfaces`), and this helper is used
 * with both.
 */
export interface WidgetEvaluationSource {
  openedQuestions: readonly ScoredQuestion[];
  closedQuestions: readonly ScoredQuestion[];
}

/**
 * Looks up the AI score for a single widget inside an evaluation result.
 *
 * Both question collections are keyed by widget id in their `question` field,
 * so a widget is either an open (free-text, AI-scored) question or a closed
 * (option-based) one — never both. Returns `null` when the evaluation has not
 * run yet or does not cover this widget.
 *
 * Shared by every `CompiledElementEvaluationProvider` implementation; the hosts
 * differ only in where they read the result from.
 */
export function findWidgetEvaluation(
  result: WidgetEvaluationSource | null | undefined,
  widgetId: string,
): EvaluationScoreResult | null {
  if (!result) {
    return null;
  }

  const openedQuestion = result.openedQuestions.find(
    (q) => q.question === widgetId,
  );
  const closedQuestion = result.closedQuestions.find(
    (q) => q.question === widgetId,
  );

  return openedQuestion ?? closedQuestion ?? null;
}

@Component({
  selector: 'lib-evaluation-score',
  standalone: true,
  imports: [DecimalPipe],
  template: `
    @let r = result();
    @let ratio = scoreRatio();
    <div
      class="mb-6 mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
    >
      <div class="flex items-center justify-between">
        <div class="text-sm font-medium text-slate-700">Punteggio</div>
      </div>
      <div class="mt-1 flex items-baseline gap-2">
        <span
          class="text-3xl font-semibold"
          [class.text-emerald-700]="ratio >= 0.8"
          [class.text-amber-600]="ratio >= 0.5 && ratio < 0.8"
          [class.text-rose-600]="ratio < 0.5"
        >
          {{ r.score | number: '1.1-1' }}
        </span>
        <span class="text-base text-slate-400">
          / {{ r.maxScore | number: '1.0-1' }}
        </span>
      </div>
      <div class="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          class="h-full rounded-full transition-all"
          [class.bg-emerald-600]="ratio >= 0.8"
          [class.bg-amber-500]="ratio >= 0.5 && ratio < 0.8"
          [class.bg-rose-600]="ratio < 0.5"
          [style.width.%]="ratio * 100"
        ></div>
      </div>
      @if (r.coverageAnalysis) {
        <div class="mt-2 text-base text-slate-600">
          {{ r.coverageAnalysis }}
        </div>
      }
      @if (r.coherenceJustification) {
        <div class="mt-2 text-sm text-slate-600">
          {{ r.coherenceJustification }}
        </div>
      }
      @if (r.coherenceWarning) {
        <div class="mt-2 flex items-start gap-1.5 text-sm text-amber-700">
          <i class="pi pi-exclamation-triangle mt-0.5 text-xs"></i>
          {{ r.coherenceWarning }}
        </div>
      }
    </div>
  `,
})
export class EvaluationScoreComponent {
  result = input.required<EvaluationScoreResult>();

  readonly scoreRatio = computed(() => {
    const r = this.result();
    if (!r?.maxScore || r.maxScore <= 0) {
      return 0;
    }

    const ratio = r.score / r.maxScore;
    return Math.max(0, Math.min(1, ratio));
  });
}
